import React from 'react';
import {
    Dialog,
    DialogContent,
    Box,
    Typography,
    TextField,
    Button,
    CircularProgress,
} from '@mui/material';
import aeropayLogo from '../../../style/images/aeroPaylogo.png';

export interface OtpVerificationDialogProps {
    /**
     * Whether the dialog is open
     */
    open: boolean;

    /**
     * Current OTP value
     */
    otp: string;

    /**
     * Whether verification is in progress
     */
    isVerifying: boolean;

    /**
     * Callback when OTP changes
     */
    onOtpChange: (otp: string) => void;

    /**
     * Callback when verify button is clicked
     */
    onVerify: () => void;

    /**
     * Callback when cancel button is clicked
     */
    onCancel: () => void;

    /**
     * Optional OTP length
     * @default 6
     */
    otpLength?: number;

    /**
     * Optional title
     */
    title?: string;

    /**
     * Optional description
     */
    description?: string;

    /**
     * Optional error message
     */
    error?: string | null;
}

/**
 * Reusable OTP Verification Dialog Component
 */
export const OtpVerificationDialog: React.FC<OtpVerificationDialogProps> = ({
    open,
    otp,
    isVerifying,
    onOtpChange,
    onVerify,
    onCancel,
    otpLength = 6,
    title = 'OTP Verification',
    description = 'A 6-digit OTP has been sent by Aeropay to your registered phone. Please enter it below.',
    error = null,
}) => {
    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && otp.length === otpLength && !isVerifying) {
            onVerify();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onCancel}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: '#141414',
                    borderRadius: '12px',
                    border: '1px solid rgba(255,255,255,0.12)',
                },
            }}
        >
            <DialogContent sx={{ p: 4 }}>
                <Box>
                    <Box display="flex" justifyContent="center" mb={4}>
                        <img
                            src={aeropayLogo}
                            alt="Aeropay"
                            style={{ height: 25 }}
                            onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                            }}
                        />
                    </Box>
                    <Typography
                        variant="h5"
                        className="text-white text-center"
                        sx={{ fontWeight: 700, mb: 1 }}
                    >
                        {title}
                    </Typography>

                    <Typography
                        sx={{ color: '#ffffff99', mb: 4, fontSize: 16, textAlign: 'center' }}
                    >
                        {description}
                    </Typography>

                    <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
                        <TextField
                            autoFocus
                            placeholder={`Enter ${otpLength} digit OTP`}
                            value={otp}
                            onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, '').slice(0, otpLength);
                                onOtpChange(value);
                            }}
                            onKeyPress={handleKeyPress}
                            inputProps={{ maxLength: otpLength }}
                            sx={{
                                width: '60%',
                                input: {
                                    color: '#fff',
                                    textAlign: 'center',
                                    letterSpacing: 6,
                                    fontSize: 20,
                                },
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': { borderColor: '#ffffff33' },
                                    '&:hover fieldset': { borderColor: '#4A7AF2' },
                                    '&.Mui-focused fieldset': { borderColor: '#4A7AF2' },
                                },
                            }}
                        />

                        {error && (
                            <Typography
                                sx={{
                                    color: '#ff4444',
                                    fontSize: 14,
                                    textAlign: 'center',
                                }}
                            >
                                {error}
                            </Typography>
                        )}

                        <Box display="flex" gap={2} width="100%" justifyContent="center">
                            <Button
                                variant="outlined"
                                onClick={onCancel}
                                disabled={isVerifying}
                                sx={{
                                    borderRadius: '50px',
                                    py: 1,
                                    px: 5,
                                    textTransform: 'none',
                                    fontSize: 16,
                                    borderColor: '#ffffff33',
                                    color: '#fff',
                                    '&:hover': {
                                        borderColor: '#ffffff66',
                                        bgcolor: 'rgba(255,255,255,0.05)',
                                    },
                                }}
                            >
                                Cancel
                            </Button>

                            <Button
                                className="btn-primary btn"
                                onClick={onVerify}
                                disabled={otp.length !== otpLength || isVerifying}
                                sx={{
                                    borderRadius: '50px',
                                    py: 1,
                                    px: 5,
                                    textTransform: 'none',
                                    fontSize: 16,
                                }}
                            >
                                {isVerifying ? (
                                    <CircularProgress size={22} sx={{ color: '#fff' }} />
                                ) : (
                                    'Verify OTP'
                                )}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default OtpVerificationDialog;